import React, { useContext, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Button } from '../../../../catalyst-components/button';
import { Input } from '../../../../catalyst-components/input';
import { Textarea } from '../../../../catalyst-components/textarea';
import { Select } from '../../../../catalyst-components/select';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Dialog, DialogBody, DialogTitle } from '../../../../catalyst-components/dialog';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

export const AdminFAQ: React.FC = () => {
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);

  // Queries
  const categories = useQuery(api.faqs.listAllCategories) || [];
  const faqs = useQuery(api.faqs.listAllFAQs) || [];

  // Mutations
  const seedFAQData = useMutation(api.faqSeed.seedFAQData);
  const createCategory = useMutation(api.faqs.createCategory);
  const updateCategory = useMutation(api.faqs.updateCategory);
  const deleteCategory = useMutation(api.faqs.deleteCategory);
  const toggleCategoryActive = useMutation(api.faqs.toggleCategoryActive);
  const createFAQMutation = useMutation(api.faqs.createFAQ);
  const updateFAQMutation = useMutation(api.faqs.updateFAQ);
  const deleteFAQMutation = useMutation(api.faqs.deleteFAQ);
  const toggleFAQActive = useMutation(api.faqs.toggleFAQActive);

  // UI state
  const [activeTab, setActiveTab] = useState<'faqs' | 'categories'>('faqs');
  const [showFAQDialog, setShowFAQDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<any>(null);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [alert, setAlert] = useState<{ open: boolean; message: string; success: boolean }>({ open: false, message: '', success: false });
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; type: 'faq' | 'category'; id?: string }>({ open: false, type: 'faq' });

  // Form state
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category_id: '',
    sort_order: 1,
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    display_name: '',
    description: '',
    sort_order: 1,
  });

  // Handlers
  const handleSeed = async () => {
    try {
      const result = await seedFAQData({ userId: convexUserId as Id<"users"> });
      setAlert({ open: true, message: result?.message || 'FAQ data seeded successfully!', success: true });
    } catch (error: any) {
      setAlert({ open: true, message: error?.message || 'Error seeding FAQ data.', success: false });
    }
  };

  // FAQ CRUD
  const openAddFAQ = () => {
    setEditingFAQ(null);
    const nextOrder = faqs.length > 0 ? Math.max(...faqs.map(f => f.sort_order)) + 1 : 1;
    setFaqForm({
      question: '',
      answer: '',
      category_id: categories[0]?._id || '',
      sort_order: nextOrder,
    });
    setShowFAQDialog(true);
  };

  const openEditFAQ = (faq: any) => {
    setEditingFAQ(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      category_id: faq.category_id,
      sort_order: faq.sort_order,
    });
    setShowFAQDialog(true);
  };

  const handleSaveFAQ = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim() || !faqForm.category_id) {
      setAlert({ open: true, message: 'Please fill in all required fields.', success: false });
      return;
    }
    try {
      if (editingFAQ) {
        await updateFAQMutation({
          id: editingFAQ._id,
          question: faqForm.question,
          answer: faqForm.answer,
          category_id: faqForm.category_id as Id<"faq_categories">,
          sort_order: faqForm.sort_order,
          updated_by: convexUserId as Id<"users">,
        });
        setAlert({ open: true, message: 'FAQ updated successfully!', success: true });
      } else {
        await createFAQMutation({
          question: faqForm.question,
          answer: faqForm.answer,
          category_id: faqForm.category_id as Id<"faq_categories">,
          sort_order: faqForm.sort_order,
          created_by: convexUserId as Id<"users">,
        });
        setAlert({ open: true, message: 'FAQ created successfully!', success: true });
      }
      setShowFAQDialog(false);
    } catch (error) {
      setAlert({ open: true, message: 'Error saving FAQ.', success: false });
    }
  };

  const handleDeleteFAQ = async () => {
    if (!confirmDelete.id) return;
    try {
      await deleteFAQMutation({ id: confirmDelete.id as Id<"faqs"> });
      setConfirmDelete({ open: false, type: 'faq' });
      setAlert({ open: true, message: 'FAQ deleted!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error deleting FAQ.', success: false });
    }
  };

  const handleToggleFAQ = async (id: Id<"faqs">) => {
    try {
      await toggleFAQActive({ id, updated_by: convexUserId as Id<"users"> });
    } catch (error) {
      setAlert({ open: true, message: 'Error toggling FAQ status.', success: false });
    }
  };

  // Category CRUD
  const openAddCategory = () => {
    setEditingCategory(null);
    const nextOrder = categories.length > 0 ? Math.max(...categories.map(c => c.sort_order)) + 1 : 1;
    setCategoryForm({
      name: '',
      display_name: '',
      description: '',
      sort_order: nextOrder,
    });
    setShowCategoryDialog(true);
  };

  const openEditCategory = (cat: any) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      display_name: cat.display_name,
      description: cat.description || '',
      sort_order: cat.sort_order,
    });
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim() || !categoryForm.display_name.trim()) {
      setAlert({ open: true, message: 'Name and display name are required.', success: false });
      return;
    }
    try {
      if (editingCategory) {
        await updateCategory({
          id: editingCategory._id,
          name: categoryForm.name,
          display_name: categoryForm.display_name,
          description: categoryForm.description || undefined,
          sort_order: categoryForm.sort_order,
          updated_by: convexUserId as Id<"users">,
        });
        setAlert({ open: true, message: 'Category updated!', success: true });
      } else {
        await createCategory({
          name: categoryForm.name,
          display_name: categoryForm.display_name,
          description: categoryForm.description || undefined,
          sort_order: categoryForm.sort_order,
          created_by: convexUserId as Id<"users">,
        });
        setAlert({ open: true, message: 'Category created!', success: true });
      }
      setShowCategoryDialog(false);
    } catch (error) {
      setAlert({ open: true, message: 'Error saving category.', success: false });
    }
  };

  const handleDeleteCategory = async () => {
    if (!confirmDelete.id) return;
    try {
      await deleteCategory({ id: confirmDelete.id as Id<"faq_categories"> });
      setConfirmDelete({ open: false, type: 'category' });
      setAlert({ open: true, message: 'Category deleted!', success: true });
    } catch (error: any) {
      setAlert({ open: true, message: error?.message || 'Error deleting category.', success: false });
    }
  };

  const handleToggleCategory = async (id: Id<"faq_categories">) => {
    try {
      await toggleCategoryActive({ id, updated_by: convexUserId as Id<"users"> });
    } catch (error) {
      setAlert({ open: true, message: 'Error toggling category status.', success: false });
    }
  };

  // Group FAQs by category for display
  const faqsByCategory = categories.map(cat => ({
    category: cat,
    faqs: faqs.filter(f => f.category_id === cat._id).sort((a, b) => a.sort_order - b.sort_order),
  }));

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button color="secondary" className="rounded-full" onClick={() => navigate('../')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-card border-2 border-primary/20">
                <QuestionMarkCircleIcon className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-1">FAQ Management</h1>
                <p className="text-base text-muted-foreground">
                  Manage user-facing FAQ content and categories
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {faqs.length === 0 && categories.length === 0 && (
                <Button color="secondary" className="rounded-full" onClick={handleSeed}>
                  <SparklesIcon className="h-4 w-4 mr-2" />
                  Seed Default FAQs
                </Button>
              )}
              <Button color="primary" className="rounded-full" onClick={openAddFAQ}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add FAQ
              </Button>
              <Button color="secondary" className="rounded-full" onClick={openAddCategory}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border/50 bg-card px-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('faqs')}
            className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'faqs'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            FAQs ({faqs.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'categories'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Categories ({categories.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div>
          {activeTab === 'faqs' ? (
            <div className="space-y-6">
              {faqsByCategory.length === 0 ? (
                <div className="text-center py-12 bg-card rounded-xl border-2 border-border/50">
                  <QuestionMarkCircleIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No FAQs yet. Add your first FAQ or seed default data.</p>
                  <Button color="primary" className="rounded-full" onClick={handleSeed}>
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Seed Default FAQs
                  </Button>
                </div>
              ) : (
                faqsByCategory.map(({ category, faqs: catFaqs }) => (
                  <div key={category._id} className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
                    <div className="bg-muted/30 px-6 py-3 border-b border-border/50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground">{category.display_name}</h3>
                        <span className="text-xs text-muted-foreground">({catFaqs.length} items)</span>
                        {!category.is_active && (
                          <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded-full border border-red-200">Inactive</span>
                        )}
                      </div>
                    </div>
                    {catFaqs.length > 0 ? (
                      <Table>
                        <TableHead>
                          <TableRow className="bg-muted/10">
                            <TableHeader className="w-8">#</TableHeader>
                            <TableHeader>Question</TableHeader>
                            <TableHeader className="w-20">Status</TableHeader>
                            <TableHeader className="w-40">Actions</TableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {catFaqs.map((faq) => (
                            <TableRow key={faq._id} className="hover:bg-muted/10">
                              <TableCell className="text-muted-foreground text-sm">{faq.sort_order}</TableCell>
                              <TableCell>
                                <div className="max-w-lg">
                                  <p className="font-medium text-foreground truncate">{faq.question}</p>
                                  <p className="text-xs text-muted-foreground truncate mt-0.5">{faq.answer.substring(0, 100)}...</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                {faq.is_active ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                    <CheckCircleIcon className="h-3.5 w-3.5" />
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                                    <XCircleIcon className="h-3.5 w-3.5" />
                                    Inactive
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1.5">
                                  <Button color="secondary" className="rounded-full" onClick={() => openEditFAQ(faq)}>
                                    <PencilIcon className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    color="secondary"
                                    className="rounded-full"
                                    onClick={() => handleToggleFAQ(faq._id)}
                                  >
                                    {faq.is_active ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                                  </Button>
                                  <Button
                                    color="red"
                                    className="rounded-full"
                                    onClick={() => setConfirmDelete({ open: true, type: 'faq', id: faq._id })}
                                  >
                                    <TrashIcon className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="px-6 py-4 text-sm text-muted-foreground">
                        No FAQs in this category yet.
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Categories Tab */
            <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
              <Table>
                <TableHead>
                  <TableRow className="bg-muted/30">
                    <TableHeader className="w-8">#</TableHeader>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Display Name</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader>FAQs</TableHeader>
                    <TableHeader className="w-20">Status</TableHeader>
                    <TableHeader className="w-40">Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <p className="text-muted-foreground">No categories yet.</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    categories.map((cat) => {
                      const faqCount = faqs.filter(f => f.category_id === cat._id).length;
                      return (
                        <TableRow key={cat._id} className="hover:bg-muted/10">
                          <TableCell className="text-muted-foreground text-sm">{cat.sort_order}</TableCell>
                          <TableCell className="font-mono text-sm">{cat.name}</TableCell>
                          <TableCell className="font-medium">{cat.display_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{cat.description || '-'}</TableCell>
                          <TableCell className="text-sm">{faqCount}</TableCell>
                          <TableCell>
                            {cat.is_active ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                <CheckCircleIcon className="h-3.5 w-3.5" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                                <XCircleIcon className="h-3.5 w-3.5" />
                                Inactive
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1.5">
                              <Button color="secondary" className="rounded-full" onClick={() => openEditCategory(cat)}>
                                <PencilIcon className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                color="secondary"
                                className="rounded-full"
                                onClick={() => handleToggleCategory(cat._id)}
                              >
                                {cat.is_active ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                              </Button>
                              <Button
                                color="red"
                                className="rounded-full"
                                onClick={() => setConfirmDelete({ open: true, type: 'category', id: cat._id })}
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* FAQ Dialog */}
      <Dialog open={showFAQDialog} onClose={() => setShowFAQDialog(false)}>
        <DialogBody>
          <DialogTitle>{editingFAQ ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
          <div className="space-y-4 mt-4">
            <Field>
              <Label>Question</Label>
              <Input
                value={faqForm.question}
                onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                placeholder="e.g., How do I get started?"
              />
            </Field>
            <Field>
              <Label>Answer</Label>
              <Textarea
                value={faqForm.answer}
                onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                placeholder="Write the answer here... Markdown is supported."
                rows={5}
              />
              <p className="text-xs text-muted-foreground mt-1">Markdown formatting is supported.</p>
            </Field>
            <Field>
              <Label>Category</Label>
              <Select
                value={faqForm.category_id}
                onChange={(e) => setFaqForm({ ...faqForm, category_id: e.target.value })}
              >
                <option value="">Select a category...</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.display_name}</option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Sort Order</Label>
              <Input
                type="number"
                min="1"
                value={faqForm.sort_order}
                onChange={(e) => setFaqForm({ ...faqForm, sort_order: parseInt(e.target.value) || 1 })}
              />
              <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first within the category.</p>
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button color="light" onClick={() => setShowFAQDialog(false)}>Cancel</Button>
            <Button color="light" onClick={handleSaveFAQ}>Save</Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onClose={() => setShowCategoryDialog(false)}>
        <DialogBody>
          <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          <div className="space-y-4 mt-4">
            <Field>
              <Label>Slug Name</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                placeholder="e.g., getting-started"
              />
              <p className="text-xs text-muted-foreground mt-1">URL-friendly identifier (lowercase, hyphens).</p>
            </Field>
            <Field>
              <Label>Display Name</Label>
              <Input
                value={categoryForm.display_name}
                onChange={(e) => setCategoryForm({ ...categoryForm, display_name: e.target.value })}
                placeholder="e.g., Getting Started"
              />
            </Field>
            <Field>
              <Label>Description (optional)</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="e.g., Learn the basics of Gruntled"
              />
            </Field>
            <Field>
              <Label>Sort Order</Label>
              <Input
                type="number"
                min="1"
                value={categoryForm.sort_order}
                onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) || 1 })}
              />
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button color="light" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button color="light" onClick={handleSaveCategory}>Save</Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Confirm Delete */}
      <ConfirmationDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, type: 'faq' })}
        onConfirm={confirmDelete.type === 'faq' ? handleDeleteFAQ : handleDeleteCategory}
        title={`Delete ${confirmDelete.type === 'faq' ? 'FAQ' : 'Category'}`}
        description={
          confirmDelete.type === 'category'
            ? 'Are you sure? Categories with existing FAQs cannot be deleted.'
            : 'Are you sure you want to delete this FAQ? This action cannot be undone.'
        }
        confirmText="Delete"
        cancelText="Cancel"
        isCritical={true}
      />

      {/* Alert */}
      <Alert open={alert.open} onClose={() => setAlert({ ...alert, open: false })} size="lg">
        <AlertTitle>{alert.success ? 'Success' : 'Error'}</AlertTitle>
        <AlertDescription>{alert.message}</AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setAlert({ ...alert, open: false })}>OK</Button>
        </AlertActions>
      </Alert>
    </div>
  );
};
